import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import * as FileSaver from 'file-saver';
import { finalize } from 'rxjs/operators';
import { PatientPortalService } from '../../_services/patient-portal/patient-portal.service';
import { SessionService } from '../../_services/session/session.service';

@Component({
  selector: 'app-online-booking-share',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './online-booking-share.component.html',
  styleUrl: './online-booking-share.component.scss',
})
export class OnlineBookingShareComponent implements OnInit {
  customerIdentifier = '';
  siteIdentifier = '';
  publicBookingUrl = '';

  loading = true;
  active = false;
  copyFeedbackKey = '';
  errorMessageKey = '';
  downloadingQr = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly patientPortalService: PatientPortalService,
    private readonly session: SessionService,
  ) {}

  ngOnInit(): void {
    const sessionCustomer = this.session.getCustomer();
    const sessionSite = this.session.getSite();
    this.customerIdentifier = `${
      sessionCustomer?.identifier ||
      this.route.snapshot.paramMap.get('customer') ||
      ''
    }`.trim();
    this.siteIdentifier = `${
      sessionSite?.identifier ||
      this.route.snapshot.paramMap.get('site') ||
      ''
    }`.trim();
    this.publicBookingUrl = `${window.location.origin}/book/${this.customerIdentifier}/${this.siteIdentifier}`;
    this.loadAvailabilityState();
  }

  openPublicBooking(): void {
    window.open(this.publicBookingUrl, '_blank', 'noopener,noreferrer');
  }

  async copyLink(): Promise<void> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.publicBookingUrl);
      } else {
        this.copyWithFallback();
      }
      this.copyFeedbackKey = 'ONLINE_BOOKING_SHARE.FEEDBACK.COPIED';
      setTimeout(() => {
        if (this.copyFeedbackKey === 'ONLINE_BOOKING_SHARE.FEEDBACK.COPIED') {
          this.copyFeedbackKey = '';
        }
      }, 2000);
    } catch {
      this.copyWithFallback();
      this.copyFeedbackKey = 'ONLINE_BOOKING_SHARE.FEEDBACK.COPIED';
    }
  }

  async downloadQrPng(): Promise<void> {
    this.downloadingQr = true;
    this.errorMessageKey = '';

    try {
      const response = await fetch(this.buildQrUrl(900));
      if (!response.ok) {
        throw new Error('QR_DOWNLOAD_FAILED');
      }
      const blob = await response.blob();
      const filename = `reserva-online-${this.siteIdentifier || 'clinic'}.png`;
      FileSaver.saveAs(blob, filename);
    } catch {
      this.errorMessageKey = 'ONLINE_BOOKING_SHARE.ERROR.QR_DOWNLOAD';
    } finally {
      this.downloadingQr = false;
    }
  }

  get qrPreviewUrl(): string {
    return this.buildQrUrl(280);
  }

  private buildQrUrl(size: number): string {
    return `https://quickchart.io/qr?size=${size}&margin=2&text=${encodeURIComponent(
      this.publicBookingUrl,
    )}`;
  }

  private copyWithFallback(): void {
    const textarea = document.createElement('textarea');
    textarea.value = this.publicBookingUrl;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  private loadAvailabilityState(): void {
    this.loading = true;
    this.patientPortalService
      .bootstrap(this.customerIdentifier, this.siteIdentifier)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.active = true;
        },
        error: () => {
          this.active = false;
        },
      });
  }
}
